import React, { useEffect, useState } from 'react';
import { useRef } from 'react';

import { lang } from '@ott/l10n';
import { useLayoutContext } from '@ott/layout-context';

import FadeIn from 'src/components/elements/FadeIn';
import GradientButton from 'src/components/elements/GradientButton';
import {
  behindPlanet,
  planetsData,
} from 'src/components/pages/loyalty/ReferralPage/blocks/Intro/planetsData';

import { getMobileDevicePlatform } from 'src/helpers/common';
import useIsWebview from 'src/hooks/useIsWebview';

import { S3_IMAGES_URL, S3_MP4_URL } from './../../constants';
import { SecondaryPlanet } from './secondaryPlanet';

import { AnimationControls, motion } from 'framer-motion';

import styles from './intro.scss';

type Props = {
  control: AnimationControls;
  isAnimating: boolean;
  setIsAnimating: (e: boolean) => void;
  direction: boolean;
  setDirection: (e: boolean) => void;
  setDisplayContent: (e: boolean) => void;
};

function Intro({
  control,
  isAnimating,
  setIsAnimating,
  direction,
  setDirection,
  setDisplayContent,
}: Props) {
  const targetRef = useRef<HTMLDivElement | null>(null);

  const { isMobile, isDesktop } = useLayoutContext();

  const mainPlanetSize = isMobile ? '280px' : '600px';

  const [documentWidth, setDocumentWidth] = useState(0);
  const [documentHeight, setDocumentHeight] = useState(0);
  const [minScreenBorder, setMinScreenBorder] = useState(0);

  const isWebview = useIsWebview();
  const webView = isWebview && !isDesktop;
  const webViewTarget = webView ? '_self' : '_blank';
  const isIos = getMobileDevicePlatform() === 'ios';

  const onCheckCollectiblesClick = (event) => {
    const link = webView
      ? 'solartrip://solartrip.com/profile/achievements'
      : `/${lang}/profile/achievements`;
    event.preventDefault();
    window.open(link, webViewTarget);
  };

  const onTellMeMoreClick = () => {
    const link = webView
      ? 'https://solartrip.com/en-us/loyalty/rewards?view=app'
      : 'https://solartrip.com/en-us/loyalty/rewards';
    window.open(link, webViewTarget);
  };

  useEffect(() => {
    setDocumentWidth(document.documentElement.clientWidth);
    setDocumentHeight(document.documentElement.clientHeight);
    document.body.style.inlineSize = '100%';
  }, []);

  useEffect(() => {
    const resize = () => {
      setDocumentWidth(() => document.documentElement.clientWidth);
      setDocumentHeight(() => document.documentElement.clientHeight);
    };
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [documentWidth, documentHeight]);

  useEffect(() => {
    setMinScreenBorder(() => Math.min(documentWidth, documentHeight));
  }, [documentWidth, documentHeight]);

  const inviteFriendsClick = () => {
    setDisplayContent(true);
    const anchor = document.getElementById('invite-friends');
    control.set('animate');
    setDirection(false);
    anchor?.scrollIntoView({ block: 'nearest' });
  };

  useEffect(() => {
    document.body.style.overflowY = isAnimating ? 'scroll' : 'auto';
    document.body.style.position = isAnimating ? 'fixed' : 'static';
  }, [isAnimating]);

  const introBackgroundStyle = {
    y: '-50%',
    x: '-50%',
    width: '100%',
    height: documentHeight + 'px',
    opacity: 0.5,
  };

  const text1Variants = {
    initial: {
      y: 0,
      opacity: 1,
      display: 'block',
      transition: {
        delay: 2.5,
        duration: 0.5,
      },
    },
    animate: {
      opacity: 0,
      y: -100,
      display: 'none',
      transition: {
        duration: 0.5,
        display: {
          delay: 1,
        },
      },
    },
  };

  const introBackgroundVariants = {
    initial: {
      height: [mainPlanetSize, minScreenBorder + 'px', documentHeight + 'px'],
      width: [mainPlanetSize, minScreenBorder + 'px', documentWidth + 'px'],
      borderRadius: '0% / 50%',
      opacity: 0.5,
      display: 'block',
      transition: {
        height: {
          times: [0, 0.5, 1],
          duration: 2,
          delay: 0.5,
        },
        width: {
          times: [0, 0.5, 1],
          duration: 2,
          delay: 0.5,
        },
        borderRadius: {
          duration: 1,
          delay: 1.5,
        },
        opacity: {
          duration: 1,
          delay: 0.5,
        },
      },
    },
    animate: {
      borderRadius: '50% / 50%',
      width: [documentWidth + 'px', minScreenBorder + 'px', mainPlanetSize],
      height: [documentHeight + 'px', minScreenBorder + 'px', mainPlanetSize],
      opacity: 0,
      display: 'none',
      transition: {
        height: {
          times: [0, 0.5, 1],
          duration: 2,
          delay: 0.5,
        },
        width: {
          times: [0, 0.5, 1],
          duration: 2,
          delay: 0.5,
        },
        borderRadius: {
          duration: 1,
          delay: 0.5,
        },
        opacity: {
          duration: 1,
          delay: 1.5,
        },
        display: {
          delay: 2.5,
        },
      },
    },
  };

  const mainPlanetVariants = {
    initial: {
      width: minScreenBorder,
      height: minScreenBorder,
      opacity: 0,
      display: 'none',
      transition: {
        delay: 0.5,
        duration: 1,
        display: {
          delay: 1.5,
        },
      },
    },
    animate: {
      width: mainPlanetSize,
      height: mainPlanetSize,
      opacity: 1,
      display: 'block',
      transition: {
        delay: 1.5,
        duration: 1,
      },
    },
  };

  const text2Variants = {
    initial: {
      y: 0,
      opacity: 0,
      display: 'none',
      transition: {
        duration: 0.5,
        display: {
          delay: 1,
        },
      },
    },
    animate: {
      opacity: 1,
      y: -50,
      display: 'block',
      transition: {
        delay: 2.5,
        duration: 0.5,
      },
    },
  };

  return (
    <motion.div className={styles.intro} ref={targetRef}>
      {isIos ? (
        <motion.img
          src={`${S3_IMAGES_URL}referral_intro.webp`}
          className={styles.introBackground}
          variants={introBackgroundVariants}
          animate={control}
          style={{ ...introBackgroundStyle, opacity: 0.8 }}
        />
      ) : (
        <motion.video
          className={styles.introBackground}
          autoPlay
          preload="auto"
          loop
          muted
          playsInline
          variants={introBackgroundVariants}
          animate={control}
          style={introBackgroundStyle}
        >
          <source src={`${S3_MP4_URL}main_screen_video.mp4`} type="video/mp4" />
        </motion.video>
      )}
      <motion.div
        className={styles.text}
        style={{
          x: '-50%',
          opacity: 1,
          position: 'fixed',
        }}
        variants={text1Variants}
        animate={control}
        onAnimationComplete={() => {
          if (!direction) {
            setIsAnimating(false);
            setDirection(true);
          }
        }}
      >
        <FadeIn className={styles.subtitle}>
          {isDesktop ? (
            <>
              <span className={styles.title}>Discover</span> the universe
              <br />
              of <span className={styles.title}>cosmic rewards</span>
              <br />
              with friends
            </>
          ) : (
            <>
              <span className={styles.title}>Discover</span>
              <br />
              the universe of
              <br />
              <span className={styles.title}>cosmic rewards</span>
              <br />
              with friends
            </>
          )}
        </FadeIn>
        <FadeIn delay={1} className={styles.description}>
          {isDesktop ? (
            <>
              Get three Planet collectibles as your friends install the
              <br />
              app or make their first bookings
            </>
          ) : (
            <>
              Get three Planet collectibles as your friends
              <br />
              install the app or make their first bookings
            </>
          )}
        </FadeIn>
        <FadeIn delay={1.5} className={styles.firstButtonsWrapper}>
          <GradientButton className={styles.headingButton} onClick={inviteFriendsClick}>
            Invite friends
          </GradientButton>
          <GradientButton
            className={styles.headingButton}
            darkGradient={true}
            onClick={(e) => onCheckCollectiblesClick(e)}
          >
            Check my collectibles
          </GradientButton>
        </FadeIn>
      </motion.div>

      <SecondaryPlanet
        direction={direction}
        isAnimating={isAnimating}
        planet={behindPlanet}
        isMobile={isMobile}
        control={control}
      />

      <motion.img
        id="animatedPlanet"
        src={`${S3_IMAGES_URL}planets/planet_3.webp`}
        data-speed={2}
        alt="mainPlanet"
        className={styles.planet}
        variants={mainPlanetVariants}
        style={{
          y: '-50%',
          x: '-50%',
          opacity: 0,
          display: 'none',
          position: 'absolute',
          width: minScreenBorder + 'px',
          height: minScreenBorder + 'px',
        }}
        animate={control}
      />

      {planetsData.map((planet) => {
        return (
          <SecondaryPlanet
            direction={direction}
            isAnimating={isAnimating}
            planet={planet}
            isMobile={isMobile}
            control={control}
            key={planet.id}
          />
        );
      })}

      <motion.div
        className={styles.text}
        initial={{
          opacity: 0,
          display: 'none',
          x: '-50%',
        }}
        variants={text2Variants}
        animate={control}
        onAnimationComplete={() => {
          if (direction) {
            setIsAnimating(false);
            setDirection(false);
            scrollBy(0, 1);
          }
        }}
      >
        <FadeIn delay={0} className={styles.subtitle}>
          What are <br />
          <span className={styles.title}>Planet collectibles?</span>
        </FadeIn>
        <FadeIn className={styles.description}>
          They are limited edition digital
          <br />
          rewards you can earn by taking
          <br />
          specific actions on Solartrip
        </FadeIn>
        <FadeIn delay={1} className={styles.secondButtonsWrapper}>
          <GradientButton
            className={styles.headingButton}
            darkGradient={true}
            onClick={() => onTellMeMoreClick()}
          >
            Tell me more
          </GradientButton>
        </FadeIn>
      </motion.div>
    </motion.div>
  );
}

export default Intro;
